import { IonIcon, IonModal, useIonRouter } from "@ionic/react";
import {
    chevronForwardOutline,
    closeOutline,
    searchOutline,
    sendOutline,
} from "ionicons/icons";
import React, { useState } from "react";
import { Following } from "../../../pages/settings/datasharing/DataSharingTypes";
import EmptyState from "../../../pages/settings/datasharing/EmptyState";
import Avatar from "../Avatar";
import { getInitials } from "../../../utils/helpers";

interface AllFollowingListDesktopProps {
    following: Following[];
    isOpen?: boolean;
    onClose?: () => void;
    onSelect?: (selected: any) => void;
    showMeOption?: boolean;
    user?: any;
}

const AllFollowingListDesktop: React.FC<AllFollowingListDesktopProps> = ({
    following,
    isOpen,
    onClose,
    onSelect,
    showMeOption = true,
    user,
}) => {
    const [searchText, setSearchText] = useState("");
    const [hoveredId, setHoveredId] = useState<number | string | null>(null);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [imageErrors, setImageErrors] = useState<{ [id: string]: boolean }>({});

    const router = useIonRouter();

    // Filter followers based on search text
    const filteredFollowers = following.filter((follower: Following) =>
        follower.name.toLowerCase().includes(searchText.toLowerCase())
    );

    const hasFollowingData = following.length > 0;

    const handleSelect = (follower: Following) => {
        const option = {
            label:
                follower.name.length > 20
                    ? follower.name.slice(0, 20) + "..."
                    : follower.name,
            value: follower.id,
        };

        if (onSelect) onSelect(option);
        if (onClose) onClose();
    };

    const closeModal = () => {
        if (onClose) onClose();
    };

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onClose}
            className="following-modal-desktop"
            style={{
                "--width": "480px",
                "--max-width": "95vw",
                "--height": "auto",
                "--max-height": "85vh",
                "--border-radius": "24px",
                "--backdrop-opacity": "0.5",
                "--box-shadow": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            } as React.CSSProperties}
        >
            <div
                style={{
                    background: "#ffffff",
                    borderRadius: "24px",
                    width: "100%",
                    maxHeight: "85vh",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
                }}
            >
                {/* Header with Gradient */}
                <div
                    style={{
                        background: "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)",
                        padding: "28px 28px 40px",
                        position: "relative",
                    }}
                >
                    {/* Close Button */}
                    <button
                        onClick={closeModal}
                        style={{
                            position: "absolute",
                            top: "20px",
                            right: "20px",
                            width: "36px",
                            height: "36px",
                            borderRadius: "50%",
                            background: "rgba(255, 255, 255, 0.2)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = "rgba(255, 255, 255, 0.35)";
                            e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                            e.currentTarget.style.transform = "scale(1)";
                        }}
                    >
                        <IonIcon
                            icon={closeOutline}
                            style={{ color: "white", fontSize: "20px" }}
                        />
                    </button>

                    {/* Title */}
                    <div style={{ textAlign: "center" }}>
                        <h2
                            style={{
                                fontSize: "24px",
                                fontWeight: "700",
                                color: "white",
                                margin: "0 0 8px",
                                letterSpacing: "-0.5px",
                                fontFamily: "Figtree, sans-serif",
                            }}
                        >
                            Following
                        </h2>
                        {hasFollowingData && (
                            <p
                                style={{
                                    color: "rgba(255, 255, 255, 0.9)",
                                    fontSize: "14px",
                                    margin: 0,
                                    fontFamily: "Figtree, sans-serif",
                                }}
                            >
                                Select a user to view their therapy data
                            </p>
                        )}
                    </div>
                </div>

                {/* Content Area with curved top */}
                <div
                    style={{
                        background: "#ffffff",
                        marginTop: "-20px",
                        borderRadius: "24px 24px 0 0",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    {/* Search Bar */}
                    {hasFollowingData && (
                        <div style={{ padding: "24px 24px 16px" }}>
                            <div style={{ position: "relative" }}>
                                <IonIcon
                                    icon={searchOutline}
                                    style={{
                                        position: "absolute",
                                        left: "16px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        fontSize: "20px",
                                        color: isSearchFocused ? "#05acd9" : "#94a3b8",
                                        transition: "color 0.2s ease",
                                        pointerEvents: "none",
                                    }}
                                />
                                <input
                                    type="text"
                                    placeholder="Search by name..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onFocus={() => setIsSearchFocused(true)}
                                    onBlur={() => setIsSearchFocused(false)}
                                    style={{
                                        width: "100%",
                                        padding: "14px 16px 14px 48px",
                                        background: "#f8fafc",
                                        border: isSearchFocused ? "2px solid #05acd9" : "2px solid #e2e8f0",
                                        borderRadius: "14px",
                                        fontSize: "15px",
                                        fontFamily: "Figtree, sans-serif",
                                        color: "#1e293b",
                                        outline: "none",
                                        transition: "all 0.2s ease",
                                        boxShadow: isSearchFocused ? "0 0 0 4px rgba(5, 172, 217, 0.1)" : "none",
                                    }}
                                />
                            </div>

                            {/* Results Count */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginTop: "12px",
                                    paddingLeft: "4px",
                                }}
                            >
                                <span
                                    style={{
                                        width: "6px",
                                        height: "6px",
                                        background: "#05acd9",
                                        borderRadius: "50%",
                                    }}
                                />
                                <span
                                    style={{
                                        fontSize: "13px",
                                        color: "#64748b",
                                        fontWeight: "500",
                                        fontFamily: "Figtree, sans-serif",
                                    }}
                                >
                                    {filteredFollowers.length}{" "}
                                    {filteredFollowers.length === 1 ? "person" : "people"} found
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Followers List */}
                    {hasFollowingData ? (
                        <div
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                padding: "0 16px 16px",
                            }}
                        >
                            {filteredFollowers.length > 0 ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    {filteredFollowers
                                        .slice()
                                        .sort((a, b) =>
                                            (a.name || "")
                                                .toLowerCase()
                                                .localeCompare((b.name || "").toLowerCase())
                                        )
                                        .map((follower) => (
                                            <div
                                                key={follower.id}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: "14px 16px",
                                                    background: hoveredId === follower.id
                                                        ? "linear-gradient(135deg, #f0fdff 0%, #e6f9fc 100%)"
                                                        : "#ffffff",
                                                    border: hoveredId === follower.id
                                                        ? "1px solid rgba(5, 172, 217, 0.3)"
                                                        : "1px solid #f1f5f9",
                                                    borderRadius: "16px",
                                                    cursor: "pointer",
                                                    gap: "14px",
                                                    transition: "all 0.2s ease",
                                                    boxShadow: hoveredId === follower.id
                                                        ? "0 4px 12px rgba(5, 172, 217, 0.15)"
                                                        : "0 1px 3px rgba(0, 0, 0, 0.04)",
                                                    transform: hoveredId === follower.id ? "translateX(4px)" : "none",
                                                }}
                                                onClick={() => handleSelect(follower)}
                                                onMouseEnter={() => setHoveredId(follower.id)}
                                                onMouseLeave={() => setHoveredId(null)}
                                            >
                                                {/* Avatar */}
                                                <div style={{ position: "relative" }}>
                                                    {(follower.avatar && !imageErrors[follower.id]) ||
                                                        follower.initials ? (
                                                        <Avatar
                                                            initials={follower.initials || getInitials(follower.name || "", "")}
                                                            imageSrc={follower.avatar}
                                                            imageClassName="rounded-[14px] border-2"
                                                            className="!w-[48px] !h-[48px]"
                                                        />
                                                    ) : (
                                                        <Avatar
                                                            initials={getInitials(follower.name || "", "")}
                                                            imageClassName="rounded-[14px]"
                                                            className="!w-[48px] !h-[48px]"
                                                        />
                                                    )}
                                                </div>

                                                {/* User Info */}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div
                                                        style={{
                                                            fontSize: "15px",
                                                            fontWeight: "600",
                                                            color: hoveredId === follower.id ? "#05acd9" : "#1e293b",
                                                            transition: "color 0.2s ease",
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            fontFamily: "Figtree, sans-serif",
                                                        }}
                                                    >
                                                        {follower.name ||
                                                            follower.firstName ||
                                                            follower.email}
                                                    </div>
                                                    {follower.email && follower.name && (
                                                        <div
                                                            style={{
                                                                fontSize: "13px",
                                                                color: "#64748b",
                                                                marginTop: "2px",
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                fontFamily: "Figtree, sans-serif",
                                                            }}
                                                        >
                                                            {follower.email}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Arrow */}
                                                <IonIcon
                                                    icon={chevronForwardOutline}
                                                    style={{
                                                        color: hoveredId === follower.id ? "#05acd9" : "#cbd5e1",
                                                        fontSize: "18px",
                                                        transition: "all 0.2s ease",
                                                        transform: hoveredId === follower.id ? "translateX(4px)" : "none",
                                                    }}
                                                />
                                            </div>
                                        ))}
                                </div>
                            ) : (
                                <div
                                    style={{
                                        textAlign: "center",
                                        padding: "40px 20px",
                                        color: "#64748b",
                                    }}
                                >
                                    <IonIcon
                                        icon={searchOutline}
                                        style={{
                                            fontSize: "48px",
                                            color: "#cbd5e1",
                                            marginBottom: "16px",
                                        }}
                                    />
                                    <p style={{ fontSize: "15px", margin: 0, fontFamily: "Figtree, sans-serif" }}>
                                        No results found for &ldquo;{searchText}&rdquo;
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ padding: "20px" }}>
                            <EmptyState
                                title="Follow an Arc Connect user"
                                description="Send a request to another person to view their therapy data."
                                buttonText="Send Request"
                                onButtonClick={() =>
                                    router.push("/settings/following?openSharing=true")
                                }
                                icon={sendOutline}
                            />
                        </div>
                    )}

                    {/* Me Option - Sticky Footer */}
                    {showMeOption && hasFollowingData && (
                        <div
                            style={{
                                padding: "16px 16px 20px",
                                borderTop: "1px solid #f1f5f9",
                                background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "14px 16px",
                                    background: "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)",
                                    borderRadius: "16px",
                                    cursor: "pointer",
                                    gap: "14px",
                                    transition: "all 0.2s ease",
                                    boxShadow: hoveredId === "me"
                                        ? "0 8px 24px rgba(5, 172, 217, 0.4)"
                                        : "0 4px 16px rgba(5, 172, 217, 0.3)",
                                    transform: hoveredId === "me" ? "translateY(-2px)" : "none",
                                }}
                                onClick={() =>
                                    handleSelect({
                                        id: user?.profile_id || 0,
                                        name: "Me",
                                        avatar: "",
                                        initials: "ME",
                                    })
                                }
                                onMouseEnter={() => setHoveredId("me")}
                                onMouseLeave={() => setHoveredId(null)}
                            >
                                {/* Avatar */}
                                <Avatar
                                    initials={getInitials(
                                        user?.first_name || "",
                                        user?.last_name || ""
                                    )}
                                    imageSrc={user?.avatar}
                                    imageClassName="rounded-[14px]"
                                    className="!w-[48px] !h-[48px] !bg-white/25 !border-white/50"
                                />

                                {/* User Info */}
                                <div style={{ flex: 1 }}>
                                    <div
                                        style={{
                                            fontSize: "15px",
                                            fontWeight: "600",
                                            color: "white",
                                            fontFamily: "Figtree, sans-serif",
                                        }}
                                    >
                                        {user?.first_name && user?.last_name
                                            ? `${user.first_name} ${user.last_name}`
                                            : "Me"}
                                    </div>
                                </div>

                                {/* Badge */}
                                <span
                                    style={{
                                        background: "rgba(255, 255, 255, 0.25)",
                                        color: "white",
                                        padding: "5px 12px",
                                        borderRadius: "20px",
                                        fontSize: "11px",
                                        fontWeight: "700",
                                        letterSpacing: "0.5px",
                                        fontFamily: "Figtree, sans-serif",
                                    }}
                                >
                                    YOU
                                </span>

                                {/* Arrow */}
                                <IonIcon
                                    icon={chevronForwardOutline}
                                    style={{
                                        color: "rgba(255, 255, 255, 0.8)",
                                        fontSize: "18px",
                                        transition: "transform 0.2s ease",
                                        transform: hoveredId === "me" ? "translateX(4px)" : "none",
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </IonModal>
    );
};

export default AllFollowingListDesktop;
